import { ErrorType } from '@zola-helpers/client/dist/es/redux/auth/authActions';
import LogService from '@zola-helpers/client/dist/es/util/logService';
import { StorefrontInquiryStatsView, InquiryView } from '@zola/svc-marketplace-ts-types';
import * as toastsActions from '@zola/zola-ui/src/actions/toastsV2Actions';

import type { AppDispatch } from '~/reducers';
import { AppThunk } from '~/reducers';
import { MappedInquiryView } from '~/types/mappedResponseTypes';
import {
  AccountVendorView,
  ConnectInquiryRequest,
  DeclineInquiryRequest,
} from '~/types/responseTypes';
import ApiService from '~/util/apiService';
import Logger, { StandardLogFn } from '~/util/logger';
import { VENDOR_SUPPORT_EMAIL } from '~/util/vendorSupportEmail';

import {
  REQUESTING_INQUIRY,
  RECEIVED_INQUIRY,
  INQUIRY_NOT_FOUND,
  RECEIVED_BOOKED_VENDORS,
  RECEIVED_STOREFRONT_INQUIRY_STATS,
  SENDING_INQUIRY_RESPONSE,
  SENT_INQUIRY_RESPONSE,
  InquiryNotFoundAction,
  ReceivedBookedVendorsAction,
  ReceivedInquiryAction,
  RequestingInquiryAction,
  ReceivedStorefrontInquiryStatsAction,
  SendingInquiryResponseAction,
  SentInquiryResponseAction,
} from './types/vendorInquiriesActionTypes';
import { requestCreditBalance } from './vendorCreditActions';

const receivedBookedVendors = (response: AccountVendorView[]): ReceivedBookedVendorsAction => ({
  type: RECEIVED_BOOKED_VENDORS,
  payload: response,
});

function requestBookedVendors(uuid: string): AppThunk<Promise<void | ReceivedBookedVendorsAction>> {
  return (dispatch) => {
    return ApiService.get(`/web-marketplace-api/v1/vendor-inquiry/${uuid}/booked-vendors`)
      .then((json: AccountVendorView[]) => dispatch(receivedBookedVendors(json)))
      .catch((err: Error) => Logger.warn(err));
  };
}

// Exported for specs
export const requestingInquiryAction = (uuid: string): RequestingInquiryAction => ({
  type: REQUESTING_INQUIRY,
  payload: {
    uuid,
  },
});

// Exported for specs
export const receivedInquiryAction = (response: MappedInquiryView): ReceivedInquiryAction => ({
  type: RECEIVED_INQUIRY,
  payload: response,
});

// Exported for specs
export const inquiryNotFoundAction = (uuid: string): InquiryNotFoundAction => ({
  type: INQUIRY_NOT_FOUND,
  payload: {
    uuid,
  },
});

export const requestInquiry = (uuid: string): AppThunk<Promise<ReceivedInquiryAction>> => {
  return (dispatch) => {
    dispatch(requestingInquiryAction(uuid));
    return ApiService.get(`/web-marketplace-api/v1/vendor-inquiry/inquiry/${uuid}`)
      .then((json: MappedInquiryView) => {
        return dispatch(receivedInquiryAction(json));
      })
      .catch((err) => {
        StandardLogFn(err);
        throw err;
      });
  };
};

export function requestInquiryDetails({ uuid }: { uuid: string }): AppThunk<Promise<void>> {
  return (dispatch) =>
    dispatch(requestInquiry(uuid))
      .then(() => dispatch(requestBookedVendors(uuid)))
      .then(() => undefined)
      .catch(() => {
        dispatch(inquiryNotFoundAction(uuid));
      });
}

const sendingInquiryResponse = (): SendingInquiryResponseAction => ({
  type: SENDING_INQUIRY_RESPONSE,
});

const sentInquiryResponse = (response: InquiryView): SentInquiryResponseAction => ({
  type: SENT_INQUIRY_RESPONSE,
  payload: response,
});

// log and notify when inquiry connect/decline requests fail
const handleInquiryError = (
  dispatch: AppDispatch,
  request: Record<string, unknown>,
  error: ErrorType
): void => {
  Logger.error(`Error sending inquiry response for ${request.inquiryUuid}`);
  Logger.error(error);

  // Post this error to the log service (web-zola/admin)
  const errorId = LogService.log(`Inquiry response error: ${error.message}`, {
    request,
    response: error.response,
  });
  dispatch(
    toastsActions.negative({
      headline: `There was an error sending your message. Please try again or contact ${VENDOR_SUPPORT_EMAIL}. Error Id: ${errorId}`,
      dismissText: 'Close',
      autoDismissInSeconds: 60,
    })
  );
  throw error;
};

export function sendInquiryConnectResponse(
  inquiryUuid: string,
  storefrontUuid: string,
  data: Partial<ConnectInquiryRequest>
): AppThunk<Promise<SentInquiryResponseAction | void>> {
  return (dispatch) => {
    dispatch(sendingInquiryResponse());
    const body: ConnectInquiryRequest = {
      inquiryUuid,
      ...data,
    };
    return ApiService.post(`/web-marketplace-api/v1/vendor-inquiry/connect/${inquiryUuid}`, body)
      .then(
        // Not mapped!
        (json: InquiryView) => {
          return dispatch(sentInquiryResponse(json));
        },
        (error) => handleInquiryError(dispatch, { inquiryUuid, ...data }, error)
      )
      .then((inquiryView) => {
        return dispatch(requestCreditBalance(storefrontUuid)).then(() => inquiryView);
      });
  };
}

export function sendInquiryDeclineResponse(
  inquiryUuid: string,
  data: Partial<DeclineInquiryRequest>
): AppThunk<Promise<SentInquiryResponseAction | void>> {
  return (dispatch) => {
    dispatch(sendingInquiryResponse());
    const body: DeclineInquiryRequest = {
      inquiryUuid,
      ...data,
    };
    return ApiService.post(
      `/web-marketplace-api/v1/vendor-inquiry/decline/${inquiryUuid}`,
      body
    ).then(
      (json) => {
        return dispatch(sentInquiryResponse(json));
      },
      (error) => handleInquiryError(dispatch, { inquiryUuid, ...data }, error)
    );
  };
}

const receivedStorefrontInquiryStats = (
  response: StorefrontInquiryStatsView
): ReceivedStorefrontInquiryStatsAction => ({
  type: RECEIVED_STOREFRONT_INQUIRY_STATS,
  payload: response,
});

export function requestStorefrontInquiryStats(storefrontUuid: string): AppThunk<Promise<void>> {
  return (dispatch) => {
    return ApiService.get(
      `/web-marketplace-api/v1/vendor-inquiry/storefront/${storefrontUuid}/stats`
    )
      .then((json: StorefrontInquiryStatsView) => {
        dispatch(receivedStorefrontInquiryStats(json));
      })
      .catch((error) => {
        Logger.warn(error);
      });
  };
}
